/**
 * Trunnion AI - ABAC Policy Simulator
 * Interactive "what-if" policy testing interface.
 */

import { useState } from 'react';
import { Play, Shield, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';

interface PolicyResult {
  decision: 'allow' | 'deny';
  matchedPolicies: Array<{
    id: string;
    name: string;
    effect: string;
    priority: number;
  }>;
  evaluationTimeMs: number;
}

export function PolicySimulator() {
  const [subjectRole, setSubjectRole] = useState('operator');
  const [subjectClearance, setSubjectClearance] = useState('secret');
  const [subjectCompartments, setSubjectCompartments] = useState('');
  const [resourceType, setResourceType] = useState('workflow');
  const [resourceClassification, setResourceClassification] = useState('secret');
  const [action, setAction] = useState('execute');
  const [result, setResult] = useState<PolicyResult | null>(null);

  const evaluateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/policies/evaluate', {
        subject: {
          role: subjectRole,
          clearance_level: subjectClearance,
          compartments: subjectCompartments
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        },
        resource: {
          type: resourceType,
          classification: resourceClassification,
        },
        action,
        environment: {},
      });
      return data as PolicyResult;
    },
    onSuccess: (data) => setResult(data),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Subject attributes */}
        <Card>
          <div className="p-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
              <Shield className="h-5 w-5 text-blue-400" />
              Subject (Who)
            </h3>
            <div className="space-y-4">
              <Select
                label="Role"
                value={subjectRole}
                onChange={(e) => setSubjectRole(e.target.value)}
                options={[
                  { value: 'viewer', label: 'Viewer' },
                  { value: 'operator', label: 'Operator' },
                  { value: 'engineer', label: 'Engineer' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
              <Select
                label="Clearance Level"
                value={subjectClearance}
                onChange={(e) => setSubjectClearance(e.target.value)}
                options={[
                  { value: 'unclassified', label: 'UNCLASSIFIED' },
                  { value: 'cui', label: 'CUI' },
                  { value: 'confidential', label: 'CONFIDENTIAL' },
                  { value: 'secret', label: 'SECRET' },
                  { value: 'top_secret', label: 'TOP SECRET' },
                ]}
              />
              <Input
                label="Compartments"
                value={subjectCompartments}
                onChange={(e) => setSubjectCompartments(e.target.value)}
                placeholder="SIGINT, HUMINT"
              />
            </div>
          </div>
        </Card>

        {/* Resource & Action */}
        <Card>
          <div className="p-6">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
              <Shield className="h-5 w-5 text-purple-400" />
              Resource & Action (What)
            </h3>
            <div className="space-y-4">
              <Select
                label="Resource Type"
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
                options={[
                  { value: 'workflow', label: 'Workflow' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'execution', label: 'Execution' },
                  { value: 'tool', label: 'Tool' },
                  { value: 'audit', label: 'Audit Log' },
                  { value: 'policy', label: 'Policy' },
                ]}
              />
              <Select
                label="Resource Classification"
                value={resourceClassification}
                onChange={(e) => setResourceClassification(e.target.value)}
                options={[
                  { value: 'unclassified', label: 'UNCLASSIFIED' },
                  { value: 'cui', label: 'CUI' },
                  { value: 'secret', label: 'SECRET' },
                  { value: 'top_secret', label: 'TOP SECRET' },
                ]}
              />
              <Select
                label="Action"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                options={[
                  { value: 'read', label: 'Read' },
                  { value: 'create', label: 'Create' },
                  { value: 'update', label: 'Update' },
                  { value: 'delete', label: 'Delete' },
                  { value: 'execute', label: 'Execute' },
                  { value: 'approve', label: 'Approve' },
                  { value: 'export', label: 'Export' },
                ]}
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Evaluate button */}
      <div className="flex justify-center">
        <Button
          onClick={() => evaluateMutation.mutate()}
          loading={evaluateMutation.isPending}
          className="px-8"
        >
          <Play className="h-4 w-4 mr-2" />
          Evaluate Policies
        </Button>
      </div>

      {/* Result */}
      {result && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Evaluation Result</h3>
              <div className="flex items-center gap-2 text-xs text-trunnion-slate-400">
                <Clock className="h-3.5 w-3.5" />
                {result.evaluationTimeMs}ms
              </div>
            </div>

            {/* Decision */}
            <div
              className={`flex items-center gap-3 rounded-xl p-4 mb-4 ${
                result.decision === 'allow'
                  ? 'bg-green-500/10 border border-green-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}
            >
              {result.decision === 'allow' ? (
                <CheckCircle className="h-8 w-8 text-green-400" />
              ) : (
                <XCircle className="h-8 w-8 text-red-400" />
              )}
              <div>
                <p className={`text-lg font-bold ${result.decision === 'allow' ? 'text-green-400' : 'text-red-400'}`}>
                  {result.decision.toUpperCase()}
                </p>
                <p className="text-sm text-trunnion-slate-400">
                  {result.matchedPolicies.length} policies matched
                </p>
              </div>
            </div>

            {/* Matched policies */}
            {result.matchedPolicies.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-trunnion-slate-300 mb-2">
                  Matched Policies
                </h4>
                <div className="space-y-2">
                  {result.matchedPolicies.map((policy) => (
                    <div
                      key={policy.id}
                      className="flex items-center justify-between rounded-lg bg-trunnion-slate-800/50 px-4 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white">{policy.name}</span>
                        <Badge variant={policy.effect === 'allow' ? 'success' : 'error'}>
                          {policy.effect}
                        </Badge>
                      </div>
                      <span className="text-xs text-trunnion-slate-500">
                        Priority: {policy.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
